import type { MachineConfig } from 'xstate';
import { Machine } from 'xstate';

import { Mode } from 'src/modules/designStudio/providers';

import { options } from './options';
import type { Event } from './types';
import { Context, Schema } from './types';

const machineConfig: MachineConfig<Context, Schema, Event> = {
  id: 'app',
  context: {
    mode: Mode.DEFAULT,
    isOnline: true,
    loadingAnimationDone: false,
    buildingLoaded: false,
    translator: () => '',
  },
  initial: 'unknown',
  states: {
    unknown: {
      always: [
        { target: 'loading', cond: 'isShareMode' },
        { target: 'fetchJob', cond: 'isGuestMode' },
        { target: 'fetchJob', cond: 'isPreviewMode' },
        { target: 'authenticating' },
      ],
    },
    authenticating: {
      invoke: {
        src: 'fetchHoverToken',
        onDone: [
          {
            target: 'fetchProfile',
          },
        ],
        onError: [
          {
            target: 'fetchProfile',
            actions: 'logError',
          },
        ],
      },
    },
    fetchProfile: {
      invoke: {
        src: 'fetchProfile',
        onDone: [
          {
            target: 'fetchJob',
            actions: ['setProfileAttributes'],
          },
        ],
        onError: [
          {
            target: 'fetchJob',
            actions: 'logError',
          },
        ],
      },
    },
    fetchJob: {
      invoke: {
        src: 'fetchJob',
        onDone: [
          {
            target: 'notFound',
            cond: 'jobNotFound',
          },
          {
            actions: ['updateJobMetadata'],
            target: 'loading',
          },
        ],
        onError: [
          {
            target: 'notFound',
            actions: 'logError',
          },
        ],
      },
    },
    loading: {
      always: {
        target: 'idle',
        cond: 'finishedLoading',
      },
      on: {
        FINISH_LOADING_ANIMATION: [{ actions: 'assignLoadingAnimationDone' }],
        FINISH_LOADING_BUILDING: [{ actions: 'assignBuildingLoaded' }],
      },
    },
    idle: {
      invoke: [{ src: 'setupNetworkListeners' }],
      on: {
        UPDATE_ONLINE_STATUS: {
          actions: ['assignOnlineStatus'],
        },
        SHOW_ERROR: {
          actions: ['showOfflineMessage'],
        },
      },
    },
    notFound: {},
  },
};

const createMachine = (initialContext: Context) =>
  Machine<Context, Schema, Event>(machineConfig, options, initialContext);

export { Schema, Context, Event, machineConfig, options, createMachine };
